/**
 * The transfer-function histogram editor (TransferFunctionHistogram.cs): density histogram on a
 * HU axis, the TF drawn as colour strips under a white alpha polyline, the dotted rendered-
 * opacity curve, hatched saturated bands when shift / stretch push the curve off the axis, the
 * orange visibility-window edges, draggable alpha points and colour stops.
 *
 * Interactions (ControlsHelp): LMB on empty plot adds an alpha point, LMB on empty palette adds
 * a colour stop, LMB drag moves either, dragging the orange edges sets the window, double-click
 * a colour stop to recolour it, RMB deletes a point or stop.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { huTicks, isNeutral, renderedOpacity, sampleAlpha, sampleColour, showsRenderedOpacity, tfEdit, toBase, toEffective, unsaturatedRange } from './histogram';
import type { RenderMode } from './presets';
import { normalizedToHu, type TfDomain, type TransferFunctionJson } from './tf';

export interface TfEditorProps {
  tf: TransferFunctionJson;
  domain: TfDomain;
  window: [number, number];
  histogram: Float32Array | null;
  huRange: [number, number];
  renderMode: RenderMode;
  samplingMultiplier: number;
  boxOpacity: number;
  title?: string;
  caption: string;
  /** `commit` is true on release / delete / recolour (the desktop EvtTransferFunctionEdited). */
  onTfChange: (tf: TransferFunctionJson, commit: boolean) => void;
  onWindowChange: (min: number, max: number, commit: boolean) => void;
}

const PLOT_H = 120;
const AXIS_H = 22;
const PALETTE_H = 26;
const HANDLE_R = 7;
const HANDLE_R_SELECTED = 8.5;
const ALPHA_HIT_PX = 14;
const EDGE_HIT_PX = 8;
const STOP_HIT_PX = 10;
const MIN_WINDOW_GAP = 0.005;
/** The page's --font-mono web font (index.html loads it); a bare ui-monospace differs per OS. */
const CANVAS_FONT = "'IBM Plex Mono', ui-monospace, monospace";
const ORANGE = 'rgb(242, 191, 51)';
const CYAN = 'rgb(80, 220, 240)';
const AMBER = 'rgb(255, 196, 0)';

type Drag = { kind: 'alpha'; index: number; moved: boolean } | { kind: 'edge'; which: 'min' | 'max' } | { kind: 'colour'; index: number; moved: boolean };

function css(rgb: [number, number, number], a = 1): string {
  return `rgba(${Math.round(rgb[0] * 255)}, ${Math.round(rgb[1] * 255)}, ${Math.round(rgb[2] * 255)}, ${a})`;
}

function toHex(rgb: { r: number; g: number; b: number }): string {
  const h = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${h(rgb.r)}${h(rgb.g)}${h(rgb.b)}`;
}

function fromHex(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function TfEditor(props: TfEditorProps) {
  const { tf, domain, window: win, histogram, huRange, renderMode, samplingMultiplier, boxOpacity, title, caption, onTfChange, onWindowChange } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colourInputRef = useRef<HTMLInputElement>(null);
  const [width, setWidth] = useState(300);
  const dragRef = useRef<Drag | null>(null);
  const [selected, setSelected] = useState<{ kind: 'alpha' | 'colour'; index: number } | null>(null);
  const [readout, setReadout] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  // Canvas text does not re-render when a web font arrives, so redraw once it has loaded.
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    let live = true;
    document.fonts
      .load(`10px ${CANVAS_FONT}`)
      .catch(() => [])
      .then(() => live && setFontReady(true));
    return () => {
      live = false;
    };
  }, []);
  const pendingColour = useRef<number | null>(null);
  const tfRef = useRef(tf);
  tfRef.current = tf;
  const height = PLOT_H + AXIS_H + PALETTE_H;
  const disabled = renderMode === 'mip';
  const alphaEditable = renderMode === 'dvr';

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(120, Math.floor(el.clientWidth))));
    ro.observe(el);
    setWidth(Math.max(120, Math.floor(el.clientWidth)));
    return () => ro.disconnect();
  }, []);

  const [unsatA, unsatB] = unsaturatedRange(domain);
  const w = width;

  // ------------------------------------------------------------ drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== w * dpr || canvas.height !== height * dpr) {
      canvas.width = w * dpr;
      canvas.height = height * dpr;
    }
    canvas.style.width = `${w}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, height);
    const h = PLOT_H;
    const grey = disabled ? 0.45 : 1;

    // plot background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fillRect(0, 0, w, h);

    // histogram bars
    if (histogram) {
      const n = histogram.length;
      const bw = w / n;
      for (let i = 0; i < n; i++) {
        const v = histogram[i];
        if (v <= 0) continue;
        const d = i / (n - 1);
        const inside = d >= win[0] && d <= win[1];
        ctx.fillStyle = inside ? 'rgba(158, 158, 158, 0.9)' : 'rgba(89, 89, 89, 0.55)';
        const bh = v * h;
        ctx.fillRect(i * bw, h - bh, Math.max(1, bw), bh);
      }
    }

    // saturated bands
    const hatch = (x0: number, x1: number, label: string) => {
      if (x1 - x0 < 1) return;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, 0, x1 - x0, h);
      ctx.clip();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.fillRect(x0, 0, x1 - x0, h);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.lineWidth = 1;
      for (let x = x0 - h; x < x1 + h; x += 8) {
        ctx.beginPath();
        ctx.moveTo(x, h);
        ctx.lineTo(x + h, 0);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = `10px ${CANVAS_FONT}`;
      ctx.textBaseline = 'top';
      ctx.textAlign = x0 === 0 ? 'left' : 'right';
      ctx.fillText(label, x0 === 0 ? x0 + 4 : x1 - 4, 4);
      ctx.restore();
    };
    if (!isNeutral(domain)) {
      hatch(0, Math.min(w, Math.max(0, unsatA * w)), 'reads TF(0)');
      hatch(Math.max(0, Math.min(w, unsatB * w)), w, 'reads TF(1)');
    }

    // TF strips and alpha polyline, on the effective axis
    const cols = Math.min(512, Math.max(32, Math.floor(w / 2)));
    const cw = w / cols;
    const alphaAt: number[] = [];
    for (let c = 0; c < cols; c++) {
      const e = (c + 0.5) / cols;
      const b = Math.min(1, Math.max(0, toBase(e, domain)));
      const a = sampleAlpha(tf, b);
      alphaAt.push(a);
      const rgb = sampleColour(tf, b);
      ctx.fillStyle = css(rgb, 0.8 * grey);
      const sh = a * h;
      ctx.fillRect(c * cw, h - sh, cw + 0.5, sh);
    }
    ctx.strokeStyle = `rgba(255, 255, 255, ${renderMode === 'dvr' ? grey : 0.35 * grey})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let c = 0; c < cols; c++) {
      const x = (c + 0.5) * cw;
      const y = h - alphaAt[c] * h;
      if (c === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // rendered-opacity dotted curve
    if (renderMode === 'dvr' && showsRenderedOpacity(samplingMultiplier, boxOpacity)) {
      ctx.save();
      ctx.setLineDash([2, 3]);
      ctx.strokeStyle = CYAN;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let c = 0; c < cols; c++) {
        const x = (c + 0.5) * cw;
        const y = h - renderedOpacity(alphaAt[c], samplingMultiplier, boxOpacity) * h;
        if (c === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();
    }

    // stretch pivot tick
    if (domain.scale !== 1) {
      ctx.fillStyle = CYAN;
      ctx.fillRect(domain.pivot * w - 1, 0, 2, 8);
    }

    // window edges
    const drawEdge = (d: number, active: boolean) => {
      const x = d * w;
      ctx.strokeStyle = ORANGE;
      ctx.lineWidth = active ? 3 : 1.5;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
      ctx.fillStyle = ORANGE;
      ctx.beginPath();
      ctx.moveTo(x - 5, 0);
      ctx.lineTo(x + 5, 0);
      ctx.lineTo(x, 7);
      ctx.closePath();
      ctx.fill();
    };
    const drag = dragRef.current;
    drawEdge(win[0], drag?.kind === 'edge' && drag.which === 'min');
    drawEdge(win[1], drag?.kind === 'edge' && drag.which === 'max');

    // alpha handles (+ injected ghost endpoints)
    const sortedAlpha = tf.alphaPoints.map((p, i) => ({ v: p.dataValue, a: p.alphaValue, i })).sort((a, b) => a.v - b.v);
    const ghosts: { v: number; a: number }[] = [];
    if (sortedAlpha.length === 0 || sortedAlpha[sortedAlpha.length - 1].v < 1) ghosts.push({ v: 1, a: 1 });
    if (sortedAlpha.length === 0 || sortedAlpha[0].v > 0) ghosts.push({ v: 0, a: 0 });
    const handle = (x: number, y: number, r: number, fill: string, stroke: string, dashed = false, pinned: 'left' | 'right' | null = null) => {
      ctx.save();
      if (dashed) ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      if (pinned) {
        ctx.setLineDash([]);
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.beginPath();
        const dir = pinned === 'left' ? -1 : 1;
        ctx.moveTo(x - dir * 2, y - 4);
        ctx.lineTo(x + dir * 2, y);
        ctx.lineTo(x - dir * 2, y + 4);
        ctx.stroke();
      }
      ctx.restore();
    };
    for (const g of ghosts) handle(toEffective(g.v, domain) * w, (1 - g.a) * h, HANDLE_R - 1, 'rgba(0,0,0,0)', 'rgba(255,255,255,0.45)', true);
    for (const p of sortedAlpha) {
      const ex = toEffective(p.v, domain);
      const sel = selected?.kind === 'alpha' && selected.index === p.i;
      const pinned = ex < 0 ? 'left' : ex > 1 ? 'right' : null;
      const x = Math.min(w - HANDLE_R, Math.max(HANDLE_R, ex * w));
      const y = (1 - p.a) * h;
      const alphaOn = alphaEditable ? grey : 0.35;
      handle(x, y, sel ? HANDLE_R_SELECTED : HANDLE_R, pinned ? `rgba(120,120,120,${alphaOn})` : `rgba(255,255,255,${0.9 * alphaOn})`, sel ? AMBER : `rgba(0,0,0,${0.7 * alphaOn})`, false, pinned);
    }

    // mode banners
    if (renderMode !== 'dvr') {
      const text = renderMode === 'iso' ? `Isosurface >= ${Math.round(normalizedToHu(win[0], huRange[0], huRange[1]))} HU — alpha curve unused` : 'TF not used in MIP — the visibility window sets brightness';
      ctx.font = `10.5px ${CANVAS_FONT}`;
      const lines = ctx.measureText(text).width > w - 8 ? text.split(' — ') : [text];
      const bh = 6 + lines.length * 13;
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(0, h - bh, w, bh);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      lines.forEach((line, i) => ctx.fillText(line, w / 2, h - bh + 3 + 6.5 + i * 13));
    }

    // HU axis
    const ay = h;
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.fillRect(0, ay, w, AXIS_H);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = `9.5px ${CANVAS_FONT}`;
    ctx.textBaseline = 'top';
    ctx.lineWidth = 1;
    const ticks = huTicks(huRange[0], huRange[1], w);
    const range = huRange[1] - huRange[0];
    ticks.forEach((hu, i) => {
      const x = ((hu - huRange[0]) / range) * w;
      ctx.beginPath();
      ctx.moveTo(x, ay);
      ctx.lineTo(x, ay + 4);
      ctx.stroke();
      ctx.textAlign = i === 0 ? 'left' : i === ticks.length - 1 ? 'right' : 'center';
      ctx.fillText(String(Math.round(hu)), Math.min(w - 1, Math.max(1, x)), ay + 6);
    });

    // palette bar
    const py = h + AXIS_H;
    const pcols = Math.min(512, Math.max(64, Math.floor(w)));
    const pcw = w / pcols;
    for (let c = 0; c < pcols; c++) {
      const e = (c + 0.5) / pcols;
      const b = Math.min(1, Math.max(0, toBase(e, domain)));
      ctx.fillStyle = css(sampleColour(tf, b), grey);
      ctx.fillRect(c * pcw, py, pcw + 0.5, PALETTE_H);
    }
    const sortedCol = tf.colourPoints.map((p, i) => ({ v: p.dataValue, c: p.colourValue, i }));
    for (const p of sortedCol) {
      const ex = toEffective(p.v, domain);
      const x = Math.min(w - HANDLE_R, Math.max(HANDLE_R, ex * w));
      const sel = selected?.kind === 'colour' && selected.index === p.i;
      const pinned = ex < 0 ? 'left' : ex > 1 ? 'right' : null;
      handle(x, py + PALETTE_H / 2, sel ? HANDLE_R_SELECTED : HANDLE_R, pinned ? 'rgba(120,120,120,1)' : css([p.c.r, p.c.g, p.c.b], grey), sel ? AMBER : 'rgba(0,0,0,0.75)', false, pinned);
    }
  }, [tf, domain, win, histogram, huRange, renderMode, samplingMultiplier, boxOpacity, w, height, selected, disabled, alphaEditable, unsatA, unsatB, fontReady]);

  // ------------------------------------------------------------ hit testing
  const local = (e: { clientX: number; clientY: number }) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const nearestAlpha = useCallback(
    (x: number, y: number): number | null => {
      let best: number | null = null;
      let bestD = ALPHA_HIT_PX;
      tfRef.current.alphaPoints.forEach((p, i) => {
        const px = Math.min(w - HANDLE_R, Math.max(HANDLE_R, toEffective(p.dataValue, domain) * w));
        const py = (1 - p.alphaValue) * PLOT_H;
        const d = Math.hypot(px - x, py - y);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    },
    [domain, w],
  );
  const nearestStop = useCallback(
    (x: number): number | null => {
      let best: number | null = null;
      let bestD = STOP_HIT_PX;
      tfRef.current.colourPoints.forEach((p, i) => {
        const px = Math.min(w - HANDLE_R, Math.max(HANDLE_R, toEffective(p.dataValue, domain) * w));
        const d = Math.abs(px - x);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    },
    [domain, w],
  );
  const nearestEdge = (x: number): 'min' | 'max' | null => {
    const dMin = Math.abs(win[0] * w - x);
    const dMax = Math.abs(win[1] * w - x);
    if (dMin > EDGE_HIT_PX && dMax > EDGE_HIT_PX) return null;
    return dMin <= dMax ? 'min' : 'max';
  };
  const inSaturated = (e: number) => !isNeutral(domain) && (e < unsatA || e > unsatB);

  // ------------------------------------------------------------ pointer handling
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const p = local(e);
    const canvas = canvasRef.current!;
    e.preventDefault();
    setMessage('');
    if (p.y < PLOT_H) {
      if (e.button === 2) {
        const i = alphaEditable ? nearestAlpha(p.x, p.y) : null;
        if (i !== null) {
          onTfChange(tfEdit.removeAlpha(tfRef.current, i), true);
          setSelected(null);
        }
        return;
      }
      if (e.button !== 0) return;
      const i = alphaEditable ? nearestAlpha(p.x, p.y) : null;
      if (i !== null) {
        dragRef.current = { kind: 'alpha', index: i, moved: false };
        setSelected({ kind: 'alpha', index: i });
        canvas.setPointerCapture(e.pointerId);
        return;
      }
      const edge = nearestEdge(p.x);
      if (edge) {
        dragRef.current = { kind: 'edge', which: edge };
        canvas.setPointerCapture(e.pointerId);
        return;
      }
      if (!alphaEditable) return;
      const ex = p.x / w;
      if (inSaturated(ex)) {
        setMessage('Cannot add a point where the curve is saturated');
        return;
      }
      const added = tfEdit.addAlpha(tfRef.current, toBase(ex, domain), 1 - p.y / PLOT_H);
      onTfChange(added.tf, false);
      dragRef.current = { kind: 'alpha', index: added.index, moved: true };
      setSelected({ kind: 'alpha', index: added.index });
      canvas.setPointerCapture(e.pointerId);
      return;
    }
    if (p.y >= PLOT_H + AXIS_H) {
      const i = nearestStop(p.x);
      if (e.button === 2) {
        if (i !== null) {
          onTfChange(tfEdit.removeColour(tfRef.current, i), true);
          setSelected(null);
        }
        return;
      }
      if (e.button !== 0) return;
      if (i !== null) {
        dragRef.current = { kind: 'colour', index: i, moved: false };
        setSelected({ kind: 'colour', index: i });
        canvas.setPointerCapture(e.pointerId);
        return;
      }
      const ex = p.x / w;
      const b = Math.min(1, Math.max(0, toBase(ex, domain)));
      const added = tfEdit.addColour(tfRef.current, b, sampleColour(tfRef.current, b));
      onTfChange(added.tf, false);
      dragRef.current = { kind: 'colour', index: added.index, moved: true };
      setSelected({ kind: 'colour', index: added.index });
      canvas.setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const p = local(e);
    const d = dragRef.current;
    if (!d) {
      if (p.y < PLOT_H) {
        const dens = Math.min(1, Math.max(0, p.x / w));
        const b = Math.min(1, Math.max(0, toBase(dens, domain)));
        const a = sampleAlpha(tfRef.current, b);
        const inside = dens >= win[0] && dens <= win[1];
        setReadout(`${Math.round(normalizedToHu(dens, huRange[0], huRange[1]))} HU · α ${a.toFixed(2)} · ${inside ? 'in window' : 'outside window'}`);
      } else setReadout('');
      return;
    }
    const ex = p.x / w;
    switch (d.kind) {
      case 'alpha': {
        const lo = Math.max(0, unsatA);
        const hi = Math.min(1, unsatB);
        const clamped = Math.min(hi, Math.max(lo, ex));
        d.moved = true;
        onTfChange(tfEdit.moveAlpha(tfRef.current, d.index, toBase(clamped, domain), 1 - p.y / PLOT_H), false);
        break;
      }
      case 'colour': {
        const b = Math.min(1, Math.max(0, toBase(Math.min(1, Math.max(0, ex)), domain)));
        d.moved = true;
        onTfChange(tfEdit.moveColour(tfRef.current, d.index, b), false);
        break;
      }
      case 'edge': {
        const v = Math.min(1, Math.max(0, ex));
        if (d.which === 'min') onWindowChange(Math.min(v, win[1] - MIN_WINDOW_GAP), win[1], false);
        else onWindowChange(win[0], Math.max(v, win[0] + MIN_WINDOW_GAP), false);
        break;
      }
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    if (canvasRef.current?.hasPointerCapture(e.pointerId)) canvasRef.current.releasePointerCapture(e.pointerId);
    if (d.kind === 'edge') onWindowChange(win[0], win[1], true);
    else if (d.moved) onTfChange(tfRef.current, true);
  };

  const onDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const p = local(e);
    if (p.y < PLOT_H + AXIS_H) return;
    const i = nearestStop(p.x);
    if (i === null) return;
    const input = colourInputRef.current;
    if (!input) return;
    pendingColour.current = i;
    input.value = toHex(tfRef.current.colourPoints[i].colourValue);
    input.click();
  };

  const onColourInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const i = pendingColour.current;
    if (i === null || i >= tfRef.current.colourPoints.length) return;
    onTfChange(tfEdit.recolour(tfRef.current, i, fromHex(e.target.value)), true);
  };

  return (
    <div className={`tf-editor ${disabled ? 'disabled' : ''}`} ref={wrapRef}>
      {title && <div className="tf-title">{title}</div>}
      <canvas
        ref={canvasRef}
        className="tf-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={() => setReadout('')}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
      />
      <input ref={colourInputRef} type="color" className="tf-colour-input" onInput={onColourInput} aria-label="Colour stop" />
      <div className="tf-caption muted small">
        {message || readout || caption}
        {!message && !readout && renderMode === 'dvr' && showsRenderedOpacity(samplingMultiplier, boxOpacity) && <span className="tf-legend">{' · dotted: rendered opacity'}</span>}
      </div>
    </div>
  );
}
